import React, {
  useState,
  useEffect,
  useRef,
  useCallback,
  useContext,
} from 'react'
import { useSearchParams } from 'react-router-dom'

import { ORGANIZATION_PERMISSION_SLUGS } from '@blocktickets/shared-utils'

import LoadingContext from '../../../context/Loading/Loading'
import UserContext from '../../../context/User/User'

import { useModal } from '../../../providers/ModalProvider/ModalProvider'
import { usePagination } from '../../../providers/PaginationProvider/PaginationProvider'

import { useTrackApiCalls } from '../../../utilities/useTrackApiCalls'
import useDebouncedValue from '../../../utilities/useDebouncedValue'
import useAbortableFetch from '../../../utilities/useAbortableFetch'

import {
  bulkUpdateEventSettings,
  getAllEventStats,
  getBulkSettingsCandidates,
  getOrgVenues,
  getSeasons,
  removeEvent,
  upload,
} from '../../../utilities/api'

import AuthService from '../../../utilities/services/auth.service'

import {
  b64toBlob,
  checkPermission,
  globalStatusOpts,
} from '../../../utilities/helpers'

import Form from 'react-bootstrap/Form'
import FloatingLabel from 'react-bootstrap/FloatingLabel'
import Stack from 'react-bootstrap/Stack'
import Card from 'react-bootstrap/Card'
import Button from 'react-bootstrap/Button'
import ProgressBar from 'react-bootstrap/ProgressBar'

import { SearchBar } from '../../SearchBar'
import { Event } from './Event'
import { LoadingContainer } from '../../LoadingContainer'
import { Pagination } from './../../Pagination'
import { Delete, Confirm } from '../../ModalContent'
import { PageLoadingContainer } from '../../PageLoadingContainer'
import { BulkEventSettingsPanel } from './BulkEventSettings'
import BulkEventSettingsResults from './BulkEventSettings/BulkEventSettingsResults'
import { runBulkSettingsUpdate } from './BulkEventSettings/bulkSettingsClient'

const DEFAULT_STATUS = 'upcoming'

function statusFromSearchParams(searchParams) {
  const value = searchParams.get('status')
  return globalStatusOpts.some((option) => option.value === value)
    ? value
    : DEFAULT_STATUS
}

export default function EventsList() {
  const didMountRef = useRef(false)
  const processingRef = useRef(false)

  const {
    isLoading: isLoadingContext,
    showLoading,
    hideLoading,
  } = useContext(LoadingContext)

  const { orgPermissions } = useContext(UserContext)

  const { incrementCallId, isLastCall } = useTrackApiCalls()

  const { openModal, closeModal } = useModal()

  const [searchParams, setSearchParams] = useSearchParams()
  const status = statusFromSearchParams(searchParams)

  const setStatus = (nextStatus) => {
    const next = new URLSearchParams(searchParams)
    next.set('status', nextStatus)
    setSearchParams(next, { replace: true })
  }
  const [seasonUUID, setSeasonUUID] = useState('')
  const [venueUUID, setVenueUUID] = useState('')
  const [seasons, setSeasons] = useState([])
  const [venues, setVenues] = useState([])

  const [isFirstTime, setIsFirstTime] = useState(true)
  const [isLoading, setIsLoading] = useState(false)
  const [query, setQuery] = useState('')
  const [events, setEvents] = useState([])
  const [total, setTotal] = useState()
  const [filteredTotal, setFilteredTotal] = useState(0)
  const [selected, setSelected] = useState({})
  const [panelOpen, setPanelOpen] = useState(false)
  const [processing, setProcessing] = useState(false)
  const [progress, setProgress] = useState(null)
  const [results, setResults] = useState(null)
  const [waitingRoomFeatureEnabled, setWaitingRoomFeatureEnabled] =
    useState(false)
  const [selectingAll, setSelectingAll] = useState(false)
  const [selectionNotice, setSelectionNotice] = useState('')
  const [selectionError, setSelectionError] = useState('')

  const debouncedQuery = useDebouncedValue(query, 300)
  const abortable = useAbortableFetch()

  const canBulkEdit =
    orgPermissions?.length > 0 &&
    checkPermission(
      AuthService.getPermissions(),
      ORGANIZATION_PERMISSION_SLUGS.EVENT_MANAGEMENT
    )

  const {
    currentPage,
    setCurrentPage,
    perPage,
    pageCount,
    setPageCount,
    resultsPerPageOpts,
    handleChange,
  } = usePagination()

  const runSearch = useCallback(
    async (isFirstTime, callId) => {
      if (isFirstTime) showLoading()
      if (!isLoading) setIsLoading(true)
      try {
        const organizationUUID = AuthService.getOrg()?.[0]?.uuid
        const res = await abortable((signal) =>
          getAllEventStats(
            {
              status,
              query: debouncedQuery,
              seasonUUID: seasonUUID || undefined,
              venueUUID: venueUUID || undefined,
              page: currentPage + 1,
              perPage: Math.min(perPage, 100),
              organizationUUID,
            },
            signal
          )
        )

        const {
          data,
          meta: {
            pagination: {
              pageCount: pc,
              total: filtered,
              allEventsTotal: total,
            },
            waitingRoomFeatureEnabled: waitingRoom,
          },
        } = res.data

        if (isLastCall(callId)) {
          setEvents(data)
          setPageCount(pc)
          setTotal(total)
          setFilteredTotal(filtered || 0)
          setWaitingRoomFeatureEnabled(Boolean(waitingRoom))
          setIsLoading(false)
        }
      } catch (err) {
        if (err?.name !== 'CanceledError') {
          console.error(err)
          setIsLoading(false)
        }
      } finally {
        if (isFirstTime) hideLoading()
        if (isLastCall(callId)) {
          setIsFirstTime(isFirstTime)
          setIsLoading(false)
        }
      }
    },
    [
      abortable,
      currentPage,
      perPage,
      debouncedQuery,
      status,
      seasonUUID,
      venueUUID,
      setPageCount,
    ]
  )

  useEffect(() => {
    const organizationUUID = AuthService.getOrg()?.[0]?.uuid
    if (!organizationUUID) return
    Promise.all([getSeasons(organizationUUID), getOrgVenues(organizationUUID)])
      .then(([seasonsRes, venuesRes]) => {
        const seasonRows = seasonsRes?.data
        setSeasons(Array.isArray(seasonRows) ? seasonRows : [])
        const venueRows = venuesRes?.data
        setVenues(Array.isArray(venueRows) ? venueRows : [])
      })
      .catch((err) => console.error(err))
  }, [])

  useEffect(() => {
    if (!didMountRef.current) return
    callApi()
  }, [currentPage, perPage])

  useEffect(() => {
    setSelected({})
    setSelectionNotice('')
    setSelectionError('')
    if (currentPage === 0 && didMountRef.current) {
      callApi()
      return
    }

    setCurrentPage(0)
  }, [debouncedQuery, status, seasonUUID, venueUUID])

  useEffect(() => {
    callApi(true)
    didMountRef.current = true
  }, [])

  const callApi = (isFirstTime = false) => {
    const callId = incrementCallId()
    runSearch(isFirstTime, callId)
  }

  const openDeleteModal = (_, eventUUID) => {
    openModal(
      Delete,
      {
        entity: 'event',
        handleDelete,
        id: eventUUID,
        onClose: closeModal,
        requireTypeToConfirm: true,
      },
      { className: '', contentClassName: '' }
    )
  }

  const handleDelete = (eventUUID) => {
    return new Promise((resolve, reject) => {
      removeEvent(eventUUID)
        .then((res) => {
          setEvents((currentEvents) =>
            currentEvents?.filter((event) => event?.eventUUID !== eventUUID)
          )
          setSelected((current) => {
            const next = { ...current }
            delete next[eventUUID]
            return next
          })
          resolve(res)
        })
        .catch((err) => {
          console.error(err)
          reject(err)
        })
    })
  }

  const toggleEvent = (event) => {
    setSelected((current) => {
      const next = { ...current }
      if (next[event.eventUUID]) delete next[event.eventUUID]
      else next[event.eventUUID] = event
      return next
    })
  }

  const pageSelected =
    events.length > 0 && events.every((event) => selected[event.eventUUID])

  const togglePage = () => {
    setSelected((current) => {
      const next = { ...current }
      if (pageSelected) {
        events.forEach((event) => {
          delete next[event.eventUUID]
        })
      } else {
        events.forEach((event) => {
          next[event.eventUUID] = event
        })
      }
      return next
    })
  }

  const selectAllMatching = async () => {
    if (selectingAll || processing) return
    setSelectingAll(true)
    setSelectionError('')
    try {
      const organizationUUID = AuthService.getOrg()?.[0]?.uuid
      const res = await getBulkSettingsCandidates({
        organizationUUID,
        status,
        query: debouncedQuery,
        seasonUUID: seasonUUID || undefined,
        venueUUID: venueUUID || undefined,
      })
      const matches = res?.data?.events || []
      setSelected(
        matches.reduce((map, event) => {
          map[event.eventUUID] = event
          return map
        }, {})
      )
      setSelectionNotice(
        res?.data?.truncated
          ? `Selected the first ${matches.length} of ${res.data.total} matching events. Narrow the filters to include the rest.`
          : ''
      )
      if (res?.data?.waitingRoomFeatureEnabled != null) {
        setWaitingRoomFeatureEnabled(
          Boolean(res.data.waitingRoomFeatureEnabled)
        )
      }
    } catch (err) {
      console.error(err)
      setSelectionError(
        err?.response?.data?.error?.message ||
          err?.response?.data?.message ||
          err?.message ||
          'Could not select matching events.'
      )
    } finally {
      setSelectingAll(false)
    }
  }

  const selectedEvents = Object.values(selected)

  const uploadImage = async (image) => {
    if (typeof image === 'number') return image
    if (typeof image !== 'string' || image.startsWith('https://')) return image
    const block = image.split(';')
    const contentType = block[0].split(':')[1]
    const realData = block[1].split(',')[1]
    const formData = new FormData()
    formData.append('files', b64toBlob(realData, contentType))
    const res = await upload(formData)
    return res?.data?.[0]?.id
  }

  const submitBulkEdit = async (patch) => {
    if (processingRef.current) return
    processingRef.current = true
    setProcessing(true)
    setPanelOpen(false)
    setProgress({ completed: 0, total: selectedEvents.length })
    try {
      const nextPatch = { ...patch }
      if (nextPatch.image) {
        nextPatch.image = await uploadImage(nextPatch.image)
      }
      const organizationUUID = AuthService.getOrg()?.[0]?.uuid
      const aggregate = await runBulkSettingsUpdate({
        events: selectedEvents,
        patch: nextPatch,
        organizationUUID,
        request: bulkUpdateEventSettings,
        onProgress: setProgress,
      })
      setResults(aggregate)
      setSelected({})
      callApi()
    } catch (err) {
      console.error(err)
      setResults({
        updated: [],
        skipped: [],
        failed: selectedEvents.map((event) => ({
          eventUUID: event.eventUUID,
          eventName: event.eventName,
          status: 'FAILED',
          reason: err?.message || 'Unexpected error',
          reasonCode: 'unexpected',
          updatedFields: [],
          timestamp: new Date().toISOString(),
        })),
      })
    } finally {
      processingRef.current = false
      setProcessing(false)
      setProgress(null)
    }
  }

  const requestBulkEdit = (patch, labels) => {
    const clearsSubcategories =
      Object.prototype.hasOwnProperty.call(patch, 'category') &&
      Array.isArray(patch.sub_categories) &&
      patch.sub_categories.length === 0
    const countLabel = `${selectedEvents.length} ${
      selectedEvents.length === 1 ? 'event' : 'events'
    }`
    openModal(
      Confirm,
      {
        title: 'Update event settings',
        description: `Apply ${labels.join(', ')} to ${countLabel}.${
          clearsSubcategories
            ? ' Changing the category clears subcategories on each event.'
            : ''
        } Each event is updated separately, and you will stay on this page to review what changed.`,
        confirmLabel: 'Update events',
        onClose: closeModal,
        onConfirm: () => {
          closeModal()
          submitBulkEdit(patch)
        },
      },
      { className: '', contentClassName: '' }
    )
  }

  return (
    <>
      {isLoadingContext ? (
        <PageLoadingContainer style="without-sidebar" />
      ) : (
        <>
          <Card body className="card--md">
            <Header
              total={total}
              isFirstTime={isFirstTime}
              setQuery={setQuery}
              status={status}
              setStatus={setStatus}
              seasonUUID={seasonUUID}
              setSeasonUUID={setSeasonUUID}
              venueUUID={venueUUID}
              setVenueUUID={setVenueUUID}
              seasons={seasons}
              venues={venues}
              canBulkEdit={canBulkEdit}
              selectedCount={selectedEvents.length}
              processing={processing}
              onBulkEdit={() => setPanelOpen(true)}
            />
            {canBulkEdit && events?.length > 0 ? (
              <Stack
                direction="horizontal"
                className="mb-3 align-items-center justify-content-between"
              >
                <Stack
                  direction="horizontal"
                  gap={3}
                  className="align-items-center"
                >
                  <Form.Check
                    type="checkbox"
                    id="select-page-events"
                    className="mb-0"
                    label={
                      pageSelected
                        ? 'Deselect this page'
                        : `Select this page (${events.length})`
                    }
                    checked={pageSelected}
                    disabled={processing}
                    onChange={togglePage}
                  />
                  {selectedEvents.length > 0 ? (
                    <span className="text-muted small">
                      {selectedEvents.length} selected
                    </span>
                  ) : null}
                </Stack>
                {filteredTotal > events.length ? (
                  <Button
                    variant="link"
                    className="p-0"
                    disabled={processing || selectingAll}
                    onClick={selectAllMatching}
                  >
                    {selectingAll
                      ? 'Selecting matching events…'
                      : `Select all ${filteredTotal} matching filters`}
                  </Button>
                ) : null}
              </Stack>
            ) : null}
            {selectionNotice ? (
              <p className="small text-muted">{selectionNotice}</p>
            ) : null}
            {selectionError ? (
              <p className="small text-danger" role="alert">
                {selectionError}
              </p>
            ) : null}
            {processing && progress ? (
              <div className="mb-3" aria-live="polite">
                <p className="small mb-1">
                  Updating events {progress.completed} of {progress.total}
                </p>
                <ProgressBar
                  now={
                    progress.total
                      ? Math.round((progress.completed / progress.total) * 100)
                      : 0
                  }
                />
              </div>
            ) : null}
            <Results
              isLoading={isLoading}
              isFirstTime={isFirstTime}
              events={events}
              total={total}
              openDeleteModal={openDeleteModal}
              canBulkEdit={canBulkEdit}
              selected={selected}
              onToggle={toggleEvent}
              processing={processing}
            />
          </Card>
          {!isLoading && events?.length > 0 && (
            <Pagination
              pageCount={pageCount}
              perPage={perPage}
              currentPage={currentPage}
              resultsPerPageOpts={resultsPerPageOpts}
              handleClick={(value) => setCurrentPage(value.selected)}
              handleChange={handleChange}
            />
          )}
          <BulkEventSettingsResults
            result={results}
            onDismiss={() => setResults(null)}
          />
        </>
      )}
      {canBulkEdit ? (
        <BulkEventSettingsPanel
          show={panelOpen}
          eventCount={selectedEvents.length}
          processing={processing}
          waitingRoomFeatureEnabled={waitingRoomFeatureEnabled}
          onClose={() => setPanelOpen(false)}
          onApply={requestBulkEdit}
        />
      ) : null}
    </>
  )
}

function Header({
  total,
  isFirstTime,
  setQuery,
  status,
  setStatus,
  seasonUUID,
  setSeasonUUID,
  venueUUID,
  setVenueUUID,
  seasons,
  venues,
  canBulkEdit,
  selectedCount,
  processing,
  onBulkEdit,
}) {
  return (
    <>
      {(!isFirstTime || total > 0) && (
        <>
          <div className="section-header-sm section-heading section-heading--primary">
            <Card.Title as="h1" className="card-title-lg">
              Events
            </Card.Title>
          </div>
          <Stack direction="horizontal" gap={3} className="mb-4 flex-wrap">
            <div className="flex-grow-1">
              <SearchBar getQuery={setQuery} placeholder="Search by name" />
            </div>
            <FloatingLabel controlId="status" label="Status">
              <Form.Select
                name="status"
                aria-label="Status"
                value={status}
                disabled={processing}
                onChange={(e) => setStatus(e.target.value)}
              >
                {globalStatusOpts.map((option, index) => (
                  <option key={index} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </Form.Select>
            </FloatingLabel>
            <FloatingLabel controlId="season" label="Season">
              <Form.Select
                name="season"
                aria-label="Season"
                value={seasonUUID}
                disabled={processing}
                onChange={(e) => setSeasonUUID(e.target.value)}
              >
                <option value="">All seasons</option>
                {seasons.map((season) => (
                  <option key={season.uuid || season.id} value={season.uuid}>
                    {season.name}
                  </option>
                ))}
              </Form.Select>
            </FloatingLabel>
            <FloatingLabel controlId="venue" label="Venue">
              <Form.Select
                name="venue"
                aria-label="Venue"
                value={venueUUID}
                disabled={processing}
                onChange={(e) => setVenueUUID(e.target.value)}
              >
                <option value="">All venues</option>
                {venues.map((venue) => (
                  <option key={venue.uuid || venue.id} value={venue.uuid}>
                    {venue.name}
                  </option>
                ))}
              </Form.Select>
            </FloatingLabel>
            {canBulkEdit ? (
              <Button
                data-testid="bulk-edit-settings"
                variant="outline-light"
                disabled={processing || selectedCount === 0}
                onClick={onBulkEdit}
              >
                Bulk edit{selectedCount > 0 ? ` (${selectedCount})` : ''}
              </Button>
            ) : null}
          </Stack>
        </>
      )}
    </>
  )
}

function Results({
  isLoading,
  events,
  isFirstTime,
  total,
  openDeleteModal,
  canBulkEdit,
  selected,
  onToggle,
  processing,
}) {
  if (isLoading) {
    return <LoadingContainer />
  }

  if (isFirstTime && total === 0) {
    return (
      <>
        <h1 className="fs-md text-center">No events.</h1>
        <p className="small text-center">
          Create an event to start selling tickets.
        </p>
      </>
    )
  }

  if (events?.length === 0) {
    return (
      <>
        <h1 className="fs-md text-center">No events matching filters.</h1>
        <p className="small text-center">
          Please adjust your search filters and try again.
        </p>
      </>
    )
  }

  return (
    <Card body className="card--sm card--dark">
      <Stack as="ul" gap={3}>
        {events.map((event, index) => (
          <li key={event?.eventUUID || index}>
            <Event
              event={event}
              openDeleteModal={openDeleteModal}
              selected={Boolean(selected?.[event?.eventUUID])}
              onToggle={canBulkEdit ? onToggle : undefined}
              disabled={processing}
            />
          </li>
        ))}
      </Stack>
    </Card>
  )
}
